/**
 * Merchant-facing targeting presets — valid TargetingRuleV1 trees only.
 * Merchants can edit any field after applying a preset.
 */

import type { TargetingRuleV1 } from "./types";

export type TargetingPreset = {
  id: string;
  label: string;
  /** Plain-language hint for the preset row */
  helpText?: string;
  /**
   * Build rule to load into the editor.
   * `null` means no targeting (show to everyone).
   */
  buildRule: () => TargetingRuleV1 | null;
};

/** Default cart threshold for “Cart value above…” (editable after apply). */
const DEFAULT_CART_MIN = 50;

export const TARGETING_PRESETS: TargetingPreset[] = [
  {
    id: "everyone",
    label: "Everyone",
    helpText: "No rules — all visitors can see this campaign",
    buildRule: () => null,
  },
  {
    id: "cart_above",
    label: "Cart value above…",
    helpText: `Cart subtotal greater than $${DEFAULT_CART_MIN} (change after apply)`,
    buildRule: () => ({
      version: "1",
      rule: {
        type: "and",
        conditions: [
          {
            type: "cart_subtotal",
            operator: "gt",
            value: DEFAULT_CART_MIN,
          },
        ],
      },
    }),
  },
  {
    id: "specific_product",
    label: "Specific product",
    helpText: "On a product page — matches the product shown (replace the ID)",
    buildRule: () => ({
      version: "1",
      rule: {
        type: "and",
        conditions: [
          {
            type: "product_id",
            operator: "equals",
            value: "gid://shopify/Product/REPLACE",
          },
        ],
      },
    }),
  },
  {
    id: "specific_collection",
    label: "Specific collection",
    helpText: "Cart includes an item from this collection (paste your collection GID)",
    buildRule: () => ({
      version: "1",
      rule: {
        type: "and",
        conditions: [
          {
            type: "cart_contains_collection",
            collectionId: "gid://shopify/Collection/REPLACE",
          },
        ],
      },
    }),
  },
  {
    id: "country",
    label: "Country is…",
    helpText: "Visitors in United States — change the country code if needed",
    buildRule: () => ({
      version: "1",
      rule: {
        type: "and",
        conditions: [
          {
            type: "geo_country",
            operator: "equals",
            value: "US",
          },
        ],
      },
    }),
  },
];
